import { FC, useMemo, useState } from "react";
import Text from "@src/lib/ui/Text";
import useLocations from "@src/features/locations/hooks/useLocations";
import { Button } from "@src/lib/ui/Button";
import ChevronDown from "@src/lib/icons/18px/chevron-down";
import { Checkbox } from "@src/lib/ui/Checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@src/lib/ui/Command";
type Props = {
  label?: string;
  placeholder?: string;
  locationIds: number[];
  setLocationIds: (x: number[]) => void;
  excludedLocationIds?: number[];
};

export const LocationMultiSelectField: FC<Props> = (props) => {
  const [open, setOpen] = useState(false);
  const { locations } = useLocations();
  const filteredLocations = useMemo(() => {
    return locations.filter((x) => !props.excludedLocationIds?.includes(x.id));
  }, [props.excludedLocationIds, locations]);
  const selectedLocationNames = useMemo(() => {
    return filteredLocations
      .filter((l) => props.locationIds.includes(l.id))
      .map((l) => l.name)
      .join(", ");
  }, [filteredLocations, props.locationIds]);
  return (
    <div
      className="flex min-w-0 flex-col gap-1 self-stretch"
      data-testid={"locations-field"}
    >
      <div className="flex justify-between self-stretch">
        <Text variant="fine-print-title" className="text-foreground">
          {props.label || "Locations"}
        </Text>
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            role="combobox"
            variant="outline"
            aria-expanded={open}
            className="pr-2 pl-3"
            data-testid="multi-select-field-input"
          >
            <div className="min-w-0 flex-1 truncate overflow-hidden text-left">
              {selectedLocationNames ||
                props.placeholder ||
                "Select locations..."}
            </div>
            <ChevronDown className="ml-2 size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="z-[9999] w-[var(--radix-popover-trigger-width)] p-0"
          disablePortal
          data-testid="locations-popover-content"
        >
          <Command>
            <CommandInput placeholder="Search locations..." className="h-9" />
            <CommandList>
              <CommandEmpty>No locations found.</CommandEmpty>
              <CommandGroup>
                {filteredLocations.map((location) => {
                  const isChecked = props.locationIds.includes(location.id);
                  return (
                    <CommandItem
                      key={location.id}
                      value={location.name}
                      data-testid="multi-select-option"
                      onSelect={() => {
                        if (isChecked) {
                          props.setLocationIds(
                            props.locationIds.filter(
                              (id) => id !== location.id,
                            ),
                          );
                        } else {
                          props.setLocationIds([
                            ...props.locationIds,
                            location.id,
                          ]);
                        }
                        setOpen(true);
                      }}
                      className="gap-2"
                    >
                      <Checkbox checked={isChecked} />
                      <Text variant="compact-description">{location.name}</Text>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default LocationMultiSelectField;
